import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { createPortal } from 'react-dom';
import { MdAutoAwesome, MdContentCopy, MdTranslate } from 'react-icons/md';
import { toast } from 'react-toastify';
import { Translations } from '../../../common/getTranslatedValue';
import { translateText } from '../ImageTranslator/imageTranslationAPIs';
import { Button } from './Button';
import { LangsTargetModal } from './LangsTargetModal';
import { Modal } from './Modal';
import { optimizeProductTitle, OptimizeProductTitleLang } from './optimizeProductTitle';
import { TextInput, TextInputProps } from './TextInput';
import { getTranslationLanguageDisplay } from './translationLanguages';

export { getLanguageTranslation, getTranslationLanguageDisplay } from './translationLanguages';

const COUNTRY_TO_GOOGLE_LANGUAGE: Record<string, string> = {
  FR: 'fr',
  BE: 'fr',
  NL: 'nl',
  DE: 'de',
  AT: 'de',
  IT: 'it',
  ES: 'es',
  PT: 'pt',
  IE: 'en',
  GB: 'en',
  EN: 'en',
};

function toGoogleTranslateLanguage(lang: string): string {
  const code = lang.toUpperCase();
  return COUNTRY_TO_GOOGLE_LANGUAGE[code] ?? code.toLowerCase();
}

function decodeHtmlEntities(value: string): string {
  const element = document.createElement('textarea');
  element.innerHTML = value;
  return element.value;
}

export function TranslationText({
  text,
  className,
  style,
}: {
  text: string | null;
  className?: string;
  style?: React.CSSProperties;
}) {
  if (text) {
    return (
      <span className={cx('whitespace-pre-wrap break-words', className)} style={style}>
        {text}
      </span>
    );
  }

  return (
    <span className={cx('text-gray-400 italic', className)} style={style}>
      Non disponible
    </span>
  );
}

export type TranslationEdit = {
  lang: string;
  newValue: string;
};

export interface TranslationFieldProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
  autoFocus?: boolean;
  size?: TextInputProps['size'];
  onKeyDown?: TextInputProps['onKeyDown'];
  disabled?: boolean;
}

function readTranslation(
  translations: Translations | string | null | undefined,
  lang: string,
): string {
  if (translations == null) return '';
  if (typeof translations === 'string') return translations;
  return translations[lang] ?? translations[lang.toUpperCase()] ?? translations[lang.toLowerCase()] ?? '';
}

export function TranslationInput({
  lang,
  translations,
  onEditTranslation,
  className,
  style,
  onKeyDown,
  multiline,
  autoFocus,
  label,
  placeholder,
  required,
  maxLength,
  rows,
  size,
  disabled,
  onTranslate,
  onOptimizeTitle,
  onCopy,
}: TranslationFieldProps & {
  lang: string;
  translations: Translations | string | null | undefined;
  onEditTranslation: (updates: TranslationEdit[]) => void;
  onTranslate?: (lang: string, text: string) => void;
  onOptimizeTitle?: (lang: string, text: string) => void;
  onCopy?: (lang: string, text: string) => void;
  className?: string;
  style?: React.CSSProperties;
}) {
  const text = readTranslation(translations, lang);
  const [draft, setDraft] = useState(text);
  const committedRef = useRef(text);

  useEffect(() => {
    setDraft(text);
    committedRef.current = text;
  }, [text]);

  const commit = (next: string) => {
    if (next === committedRef.current) return;
    committedRef.current = next;
    onEditTranslation([{ lang, newValue: next }]);
  };

  const filledText = draft.trim();

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0">
        <TextInput
          label={label}
          placeholder={placeholder}
          required={required}
          maxLength={maxLength}
          multiline={multiline}
          rows={rows}
          size={size}
          disabled={disabled}
          autoFocus={autoFocus}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !multiline) {
              commit(event.currentTarget.value);
            }
            onKeyDown?.(event);
          }}
          className={className}
          style={style}
        />
      </div>
      {(onTranslate || onOptimizeTitle || onCopy) && filledText && !disabled && (
        <div className={cx('flex items-start gap-1 shrink-0', label && 'mt-6')}>
          {onTranslate && (
            <Button
              type="button"
              preset="white"
              size="sm"
              className="!px-2 !py-2"
              tooltip="Traduire"
              aria-label="Traduire"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                commit(draft);
                onTranslate(lang, draft);
              }}
            >
              <MdTranslate className="w-5 h-5" />
            </Button>
          )}
          {onOptimizeTitle && (
            <Button
              type="button"
              preset="white"
              size="sm"
              className="!px-2 !py-2"
              tooltip="Optimiser le titre"
              aria-label="Optimiser le titre"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                commit(draft);
                onOptimizeTitle(lang, draft);
              }}
            >
              <MdAutoAwesome className="w-5 h-5" />
            </Button>
          )}
          {onCopy && (
            <Button
              type="button"
              preset="white"
              size="sm"
              className="!px-2 !py-2"
              tooltip="Copier"
              aria-label="Copier"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                commit(draft);
                onCopy(lang, draft);
              }}
            >
              <MdContentCopy className="w-5 h-5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

type LangsTargetMode = 'translate' | 'copy' | 'optimize-title';

function langsTargetTitle(mode: LangsTargetMode, sourceLang: string): string {
  const source = getTranslationLanguageDisplay(sourceLang);
  const sourceLabel = `${source.flag} ${source.name}`;
  if (mode === 'copy') return `Copier depuis ${sourceLabel}`;
  if (mode === 'optimize-title') return `Optimiser le titre depuis ${sourceLabel}`;
  return `Traduire depuis ${sourceLabel}`;
}

function langsTargetLanguages(
  mode: LangsTargetMode,
  sourceLang: string,
  languages: readonly string[],
): readonly string[] {
  if (mode === 'optimize-title') return languages;
  const sourceCode = getTranslationLanguageDisplay(sourceLang).code;
  return languages.filter((language) => getTranslationLanguageDisplay(language).code !== sourceCode);
}

function toLangParameters(lang: string): OptimizeProductTitleLang {
  const display = getTranslationLanguageDisplay(lang);
  return {
    lang: display.code,
    name: display.name,
    language: toGoogleTranslateLanguage(display.code),
  };
}

function limitTitle(title: string, maxLength: number | undefined): string {
  if (maxLength != null && title.length > maxLength) {
    return title.slice(0, maxLength);
  }
  return title;
}

export interface TranslatedValueModalProps extends Omit<TranslationFieldProps, 'autoFocus'> {
  translations: Translations | string | null | undefined;
  languages: readonly string[];
  activeLang: string;
  onSelectLang: (lang: string) => void;
  onClose: () => void;
  onEditTranslation: (updates: TranslationEdit[]) => void;
  optimizeTitle?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const TranslatedValueModal: React.FC<TranslatedValueModalProps> = ({
  translations,
  languages,
  activeLang,
  onSelectLang,
  onClose,
  onEditTranslation,
  optimizeTitle,
  className,
  style,
  label,
  placeholder,
  maxLength,
  multiline,
  rows,
  size,
  disabled,
  onKeyDown,
}) => {
  const activeCode = activeLang.toUpperCase();
  const [targetsRequest, setTargetsRequest] = useState<{
    lang: string;
    text: string;
    mode: LangsTargetMode;
  } | null>(null);

  const handleConfirmTargets = async (selectedLanguages: string[]) => {
    if (!targetsRequest) return;
    const { lang: sourceLang, text: sourceText, mode } = targetsRequest;

    if (mode === 'copy') {
      onEditTranslation(selectedLanguages.map((lang) => ({
        lang,
        newValue: limitTitle(sourceText, maxLength),
      })));
      return;
    }

    if (mode === 'optimize-title') {
      try {
        const titles = await optimizeProductTitle({
          initialTitle: sourceText,
          initialLang: toLangParameters(sourceLang),
          maxLength: maxLength ?? null,
          targetLangs: selectedLanguages.map((lang) => toLangParameters(lang)),
        });
        if (titles.length === 0) {
          toast.error('L\'optimisation du titre a échoué');
          return;
        }
        onEditTranslation(titles.map((item) => ({
          lang: item.lang,
          newValue: limitTitle(item.title, maxLength),
        })));
        if (titles.length < selectedLanguages.length) {
          toast.error('Certaines optimisations ont échoué');
        }
      } catch (error) {
        toast.error(error instanceof Error && error.message
          ? error.message
          : 'L\'optimisation du titre a échoué');
      }
      return;
    }

    const sourceGoogle = toGoogleTranslateLanguage(sourceLang);
    const settled = await Promise.allSettled(selectedLanguages.map(async (targetLang) => {
      const targetGoogle = toGoogleTranslateLanguage(targetLang);
      let translated = sourceText;
      if (sourceGoogle !== targetGoogle) {
        const [result] = await translateText([sourceText], sourceGoogle, targetGoogle);
        if (typeof result !== 'string') {
          throw new Error('Empty translation');
        }
        translated = decodeHtmlEntities(result);
      }
      translated = limitTitle(translated, maxLength);
      return { targetLang, translated };
    }));

    const updates: TranslationEdit[] = [];
    for (const result of settled) {
      if (result.status === 'fulfilled') {
        updates.push({
          lang: result.value.targetLang,
          newValue: result.value.translated,
        });
      }
    }
    if (updates.length > 0) {
      onEditTranslation(updates);
    }

    const failedCount = settled.filter((result) => result.status === 'rejected').length;
    if (failedCount > 0) {
      toast.error(failedCount === settled.length
        ? 'La traduction a échoué'
        : 'Certaines traductions ont échoué');
    }
  };

  return createPortal(
    <div
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Modal
        title="Traductions"
        onClose={onClose}
        width="medium"
        noContentPadding
      >
        <div className="divide-y divide-gray-200">
          {languages.map((language) => {
            const display = getTranslationLanguageDisplay(language);
            const isActive = display.code === activeCode;

            return (
              <div
                key={language}
                className={cx(
                  'px-4 py-3 flex items-start gap-3',
                  isActive && 'bg-blue-50',
                )}
              >
                <button
                  type="button"
                  className="w-36 shrink-0 flex items-start gap-3 text-left cursor-pointer rounded-md hover:bg-gray-100 p-1 -m-1"
                  onClick={() => {
                    onSelectLang(display.code);
                    onClose();
                  }}
                >
                  <span className="text-2xl leading-none shrink-0">{display.flag}</span>
                  <span className="min-w-0">
                    <span className="block font-medium text-gray-900">{display.name}</span>
                    <span className="block text-xs text-gray-500">
                      {display.code}
                      {isActive ? ' · Actif' : ''}
                    </span>
                  </span>
                </button>
                <div className="flex-1 min-w-0 text-gray-800 break-words">
                  <TranslationInput
                    lang={display.code}
                    translations={translations}
                    onEditTranslation={onEditTranslation}
                    className={className}
                    style={style}
                    label={label}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    multiline={multiline}
                    rows={rows}
                    size={size}
                    disabled={disabled}
                    onKeyDown={onKeyDown}
                    onTranslate={(lang, text) => setTargetsRequest({ lang, text, mode: 'translate' })}
                    onOptimizeTitle={optimizeTitle
                      ? (lang, text) => setTargetsRequest({ lang, text, mode: 'optimize-title' })
                      : undefined}
                    onCopy={(lang, text) => setTargetsRequest({ lang, text, mode: 'copy' })}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
      {targetsRequest && (
        <LangsTargetModal
          title={langsTargetTitle(targetsRequest.mode, targetsRequest.lang)}
          sourceText={targetsRequest.text}
          languages={langsTargetLanguages(targetsRequest.mode, targetsRequest.lang, languages)}
          translations={translations}
          onClose={() => setTargetsRequest(null)}
          onConfirm={handleConfirmTargets}
        />
      )}
    </div>,
    document.body,
  );
};

export default TranslatedValueModal;
